import { initializeBingTracking } from "../analytics_initialization/BingAnalyticsInitialization";
import {
	initializeGATracking,
	initializeGtagTracking,
} from "../analytics_initialization/GoogleAnalyticsInitialization";
import { InitializedTrackingManager } from "./InitializedTrackingManager";
import { TrackingCodes } from "../tracking_codes/TrackingCodes";
import { TrackingManagerState } from "./TrackingManagerState";
import {
	TrackedEventData,
	GaEventData,
	InternalEventData,
} from "../types/EventsData";
import { AnalyticsTracker } from "../trackers/AnalyticsTracker";
import { IdleAnalyticsTracker } from "../trackers/IdleAnalyticsTracker";
import { TrackingScope } from "../utilities/tracking_permissions/TrackingScope";
import { InternalAnalyticsTracker } from "../trackers/InternalAnalyticsTracker";
import { BingAnalyticsTracker } from "../trackers/BingAnalyticsTracker";
import { DefaultContractSectionsTracker } from "../trackers/DefaultContractSectionsTracker";
import { ContractSectionsTracker } from "../trackers/ContractSectionsTracker";
import { IdleContractSectionsTracker } from "../trackers/IdleContractSectionsTracker";

enum EventType {
	Ga,
	Internal,
	Tracked,
	Sharing,
	Signed,
	ContractSection,
}

type ContractSectionEventData = { section: string; group: string };

type EventsData =
	| TrackedEventData
	| GaEventData
	| InternalEventData
	| string
	| ContractSectionEventData;

/**
 * This class is responsible for the initialization of the tracking manager and
 * handling tracking events while the TrackingManager is in the process of initialization.
 */
export class PendingInitializationTrackingManager extends TrackingManagerState {
	private readonly eventsData: {
		type: EventType;
		data: EventsData;
	}[] = [];

	async initialize(): Promise<void> {
		const initializedTrackingManager =
			await this.createInitializedTrackingManager();

		await this.trackingManager.transitionToState(initializedTrackingManager);

		this.submitEventsData();
	}

	trackSharingEvent(webuserDataId: string): void {
		this.eventsData.push({
			type: EventType.Sharing,
			data: webuserDataId,
		});
	}

	trackSignedEvent(webuserDataId: string): void {
		this.eventsData.push({
			type: EventType.Signed,
			data: webuserDataId,
		});
	}

	submitTrackedEvent(eventData: TrackedEventData): void {
		this.eventsData.push({
			type: EventType.Tracked,
			data: eventData,
		});
	}

	submitInternalEvent(eventData: InternalEventData): void {
		this.eventsData.push({
			type: EventType.Internal,
			data: eventData,
		});
	}

	submitGAEvent(eventData: GaEventData): void {
		this.eventsData.push({
			type: EventType.Ga,
			data: eventData,
		});
	}

	trackContractSection(newSection: string, newGroup: string): void {
		this.eventsData.push({
			type: EventType.ContractSection,
			data: {
				section: newSection,
				group: newGroup,
			},
		});
	}

	private async createInitializedTrackingManager(): Promise<TrackingManagerState> {
		let googleAnalyticsTracker: AnalyticsTracker<GaEventData> =
			new IdleAnalyticsTracker();
		let bingAnalyticsTracker: AnalyticsTracker<undefined> =
			new IdleAnalyticsTracker();
		let internalAnalyticsTracker: AnalyticsTracker<InternalEventData> =
			new IdleAnalyticsTracker();
		let contractSectionsTracker: ContractSectionsTracker =
			new IdleContractSectionsTracker();

		const permissionChecker =
			this.serviceProvider.getScopedTrackingPermissionChecker();
		if (permissionChecker.isTrackingEnabled(TrackingScope.Environment)) {
			const isUserLicensed = await this.checkIfUserLicensed();

			if (permissionChecker.isTrackingEnabled(TrackingScope.ThirdParty)) {
				const trackingCodesProvider =
					this.serviceProvider.getTrackingCodesProvider();
				const trackingCodes = await trackingCodesProvider.provideCodes();
				googleAnalyticsTracker = this.initializeGoogleAnalyticsTracker(
					trackingCodes,
					isUserLicensed
				);
				bingAnalyticsTracker = this.initializeBingTracker(trackingCodes);
			}

			if (permissionChecker.isTrackingEnabled(TrackingScope.Internal)) {
				internalAnalyticsTracker = new InternalAnalyticsTracker(
					isUserLicensed,
					this.serviceProvider
				);
				contractSectionsTracker = new DefaultContractSectionsTracker(
					this.serviceProvider,
					isUserLicensed
				);
			}
		}

		return new InitializedTrackingManager(
			googleAnalyticsTracker,
			bingAnalyticsTracker,
			internalAnalyticsTracker,
			contractSectionsTracker
		);
	}

	private checkIfUserLicensed(): Promise<boolean> {
		return this.serviceProvider
			.getUserLicenseStatusChecker()
			.checkIfLicensed(this.executionContext.productCode);
	}

	private submitEventsData(): void {
		for (const { type, data } of this.eventsData) {
			this.submitEventData(type, data);
		}
	}

	private submitEventData(event: EventType, data: EventsData) {
		const trackingManager = this.trackingManager;
		switch (event) {
			case EventType.Ga:
				trackingManager.submitGAEvent(data as GaEventData);
				break;
			case EventType.Internal:
				trackingManager.submitInternalEvent(data as InternalEventData);
				break;
			case EventType.Sharing:
				trackingManager.trackSharingEvent(data as string);
				break;
			case EventType.Signed:
				trackingManager.trackSignedEvent(data as string);
				break;
			case EventType.Tracked:
				trackingManager.submitTrackedEvent(data as TrackedEventData);
				break;
			case EventType.ContractSection: {
				const contractSectionEventData = data as ContractSectionEventData;
				trackingManager.trackContractSection(
					contractSectionEventData.section,
					contractSectionEventData.group
				);
				break;
			}
		}
	}

	private initializeBingTracker(
		trackingCodes: TrackingCodes
	): AnalyticsTracker<undefined> {
		if (trackingCodes.bingTrackingCode === undefined) {
			return new IdleAnalyticsTracker();
		}

		const sendBingEvent = initializeBingTracking(
			trackingCodes.bingTrackingCode
		);

		return new BingAnalyticsTracker(sendBingEvent, this.serviceProvider);
	}

	private initializeGoogleAnalyticsTracker(
		trackingCodes: TrackingCodes,
		isUserLicensed: boolean
	): AnalyticsTracker<GaEventData> {
		if (trackingCodes.gaTrackingCode === undefined) {
			return new IdleAnalyticsTracker();
		}

		const ga = initializeGATracking(
			this.executionContext.loginId,
			trackingCodes.gaTrackingCode,
			trackingCodes.gaTrackingCodeUrl
		);

		const gtag =
			trackingCodes.gtagCodes.length === 0
				? () => {
						// do nothing
				  }
				: initializeGtagTracking(
						trackingCodes.gtagCodes,
						trackingCodes.ga4Code
				  );

		const googleAnalyticsFunctions = {
			ga,
			gtag,
		};
		const googleAnalyticsTrackerFactory =
			this.serviceProvider.getGoogleAnalyticsTrackerFactory();
		return googleAnalyticsTrackerFactory.createTracker({
			trackingCodes,
			isUserLicensed,
			googleAnalyticsFunctions,
		});
	}
}
